import { useStoreStockDetail } from '@gozoki/api'
import type {
  StoreStockDetailCategory,
  StoreStockDetailProduct,
  StoreStockDetailResponse,
} from '@gozoki/api-types'
import { capitalizeFirstLetter, frenchFormat, normalizeSearchText, pluralWord } from '@gozoki/tools'
import { Skeleton } from '@gozoki/ui/components/ui/skeleton'
import { cn } from '@gozoki/ui/lib/utils'
import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { LayerIcon } from './icons/LayerIcon'
import { ChevronDownIcon, SearchIcon } from './icons/StockDetailIcons'
import { KioskModal, ModalBody, ModalKpi, ModalPanel } from './ui/KioskModal'

const PARIS = 'Europe/Paris'
const ORANGE = '#E0922F'
const RED = '#E25B53'
/** Une rupture prévue à moins de 2 h passe en orange : c'est le délai pour réagir en boutique. */
const SOON_STOCKOUT_MINUTES = 120

type StockFilter = 'all' | 'low' | 'out'

const matchesFilter = (product: StoreStockDetailProduct, filter: StockFilter) =>
  filter === 'all' || product.status === filter

/** « Aujourd'hui 14:00 », « Demain 06:00 », sinon « Lun. 19/10 06:00 ». */
const formatDelivery = (iso: string | null): string => {
  if (!iso) return '—'
  const at = DateTime.fromISO(iso).setZone(PARIS).setLocale('fr')
  const today = DateTime.now().setZone(PARIS).startOf('day')
  const days = Math.round(at.startOf('day').diff(today, 'days').days)
  if (days === 0) return `Aujourd’hui ${at.toFormat('HH:mm')}`
  if (days === 1) return `Demain ${at.toFormat('HH:mm')}`
  return `${capitalizeFirstLetter(at.toFormat('ccc'))} ${at.toFormat('dd/MM HH:mm')}`
}

const isSoon = (iso: string | null) =>
  iso !== null && DateTime.fromISO(iso).diffNow('minutes').minutes <= SOON_STOCKOUT_MINUTES

function StockoutCell({
  stockoutAt,
  outCount = 0,
  isOut = false,
}: {
  stockoutAt: string | null
  outCount?: number
  isOut?: boolean
}) {
  if (isOut) return <span style={{ color: RED }}>Rupture</span>
  if (outCount > 0) {
    return (
      <span style={{ color: RED }}>
        {outCount} {pluralWord(outCount, 'rupture')}
      </span>
    )
  }
  if (!stockoutAt) return <span>—</span>
  return (
    <span style={isSoon(stockoutAt) ? { color: ORANGE } : undefined}>
      {DateTime.fromISO(stockoutAt).setZone(PARIS).toFormat('HH:mm')}
    </span>
  )
}

const stockColor = (status: StoreStockDetailProduct['status']) => {
  if (status === 'out') return RED
  if (status === 'low') return ORANGE
  return undefined
}

// Indicateurs séparés par un filet vertical : marge de part et d'autre, sauf aux extrémités.
const KPI_SPACING = 'px-[16px] first:pl-0 last:pr-0'

function Summary({ data, updatedAt }: { data: StoreStockDetailResponse; updatedAt: number }) {
  return (
    <ModalPanel className="flex shrink-0 items-center justify-between gap-[24px] bg-[#FBFAF6] p-[17px]">
      <div className="flex flex-col gap-[4px]">
        <span className="font-sans text-[14px] leading-[16px] font-medium text-[#6C7A6B]">
          Stock restant · mis à jour à{' '}
          {DateTime.fromMillis(updatedAt).setZone(PARIS).toFormat('HH:mm')}
        </span>
        <span className="font-brand text-[40px] leading-[44px] font-extrabold text-[#354A31]">
          {frenchFormat(data.totalStock)} UVC
        </span>
      </div>

      <div className="flex items-stretch divide-x divide-[#0000001A]">
        <ModalKpi
          className={KPI_SPACING}
          label="Références en stock"
          value={`${data.inStockCount} / ${data.referenceCount}`}
          color="#6A9560"
        />
        <ModalKpi
          className={KPI_SPACING}
          label="Stock faible"
          value={String(data.lowStockCount)}
          color={ORANGE}
        />
        <ModalKpi
          className={KPI_SPACING}
          label="Ruptures"
          value={String(data.outOfStockCount)}
          color={RED}
        />
        <ModalKpi
          className={KPI_SPACING}
          label="Prochaine livraison"
          value={formatDelivery(data.nextDeliveryAt)}
          color="#354A31"
        />
      </div>
    </ModalPanel>
  )
}

function FilterChip({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'cursor-pointer rounded-full border px-[10px] py-[5px] font-sans text-[12px] leading-[14px] font-medium whitespace-nowrap',
        active
          ? 'border-[#8DAA7E] bg-[#CEE3C4] text-[#354A31]'
          : 'border-[#0000001A] bg-white text-[#354A31]',
      )}
    >
      {label}
    </button>
  )
}

// Colonnes communes à l'en-tête et aux lignes : libellé fluide, puis Stock, Vendus, Rupture et le
// chevron en largeur fixe.
const COLUMNS = 'grid grid-cols-[minmax(0,1fr)_100px_100px_120px_32px] items-center gap-x-[8px]'

function CategoryGroup({
  category,
  products,
  open,
  onToggle,
}: {
  category: StoreStockDetailCategory
  products: StoreStockDetailProduct[]
  open: boolean
  onToggle: () => void
}) {
  return (
    <div className="shrink-0 overflow-hidden rounded-[12px] border border-[#0000000F] bg-[#FBFAF6]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn(COLUMNS, 'w-full cursor-pointer px-[8px] py-[8px] text-left')}
      >
        <span className="flex min-w-0 items-center gap-[12px]">
          <span className="flex size-[36px] shrink-0 items-center justify-center rounded-[8px] bg-[#F0EDE2] text-[#4F6A47]">
            <LayerIcon className="size-[22px]" />
          </span>
          <span className="truncate font-sans text-[14px] leading-[16px] font-semibold text-[#354A31]">
            {category.label}
          </span>
        </span>
        <span
          className="font-sans text-[13px] font-semibold text-[#354A31]"
          style={category.belowMinimum ? { color: ORANGE } : undefined}
        >
          {frenchFormat(category.stock)}
        </span>
        <span className="font-sans text-[13px] font-semibold text-[#354A31]">
          {frenchFormat(category.soldToday)}
        </span>
        <span className="font-sans text-[13px] font-semibold text-[#354A31]">
          <StockoutCell stockoutAt={category.stockoutAt} outCount={category.outOfStockCount} />
        </span>
        <span className="flex justify-center text-[#354A31]">
          <ChevronDownIcon open={open} />
        </span>
      </button>

      {open ? (
        <div className="mx-[8px] border-t border-[#0000000F] py-[8px]">
          {products.map((product) => (
            <div key={product.reference} className={cn(COLUMNS, 'px-[12px] py-[10px]')}>
              <span className="truncate font-sans text-[14px] leading-[16px] font-medium text-[#354A31]">
                {product.label}
              </span>
              <span
                className="font-sans text-[13px] font-semibold text-[#354A31]"
                style={{ color: stockColor(product.status) }}
              >
                {frenchFormat(product.stock)}
              </span>
              <span className="font-sans text-[13px] font-semibold text-[#354A31]">
                {frenchFormat(product.soldToday)}
              </span>
              <span className="font-sans text-[13px] font-semibold text-[#354A31]">
                <StockoutCell stockoutAt={product.stockoutAt} isOut={product.status === 'out'} />
              </span>
              <span />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function ProductDetail({ data }: { data: StoreStockDetailResponse }) {
  const [filter, setFilter] = useState<StockFilter>('all')
  const [search, setSearch] = useState('')
  // Ouverture par défaut : les catégories qui ont un produit en stock faible ou en rupture.
  const [openIds, setOpenIds] = useState<Set<number>>(
    () =>
      new Set(
        data.categories
          .filter((category) => category.lowStockCount + category.outOfStockCount > 0)
          .map((category) => category.id),
      ),
  )

  const query = normalizeSearchText(search.trim())
  const filtering = filter !== 'all' || query !== ''
  const groups = useMemo(
    () =>
      data.categories
        .map((category) => ({
          category,
          products: category.products.filter(
            (product) =>
              matchesFilter(product, filter) &&
              (!query || normalizeSearchText(product.label).includes(query)),
          ),
        }))
        .filter(({ products }) => !filtering || products.length > 0),
    [data.categories, filter, filtering, query],
  )

  const toggle = (id: number) =>
    setOpenIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <ModalPanel className="flex min-h-0 flex-1 flex-col gap-[12px] p-[17px]">
      <div className="flex shrink-0 items-center justify-between gap-[16px]">
        <span className="flex items-baseline gap-[8px]">
          <span className="font-sans text-[15px] leading-[18px] font-semibold text-[#354A31]">
            Détail des produits
          </span>
          <span className="font-sans text-[14px] leading-[16px] font-medium text-[#6C7A6B]">
            {data.referenceCount} {pluralWord(data.referenceCount, 'référence')}
          </span>
        </span>

        <div className="flex items-center gap-[8px]">
          <FilterChip active={filter === 'all'} label="Tous" onClick={() => setFilter('all')} />
          <FilterChip
            active={filter === 'low'}
            label={`Stock faible · ${data.lowStockCount}`}
            onClick={() => setFilter('low')}
          />
          <FilterChip
            active={filter === 'out'}
            label={`Ruptures · ${data.outOfStockCount}`}
            onClick={() => setFilter('out')}
          />
          <label className="ml-[4px] flex h-[42px] w-[280px] items-center gap-[8px] rounded-[8px] border border-[#0000001A] bg-white px-[12px] text-[#6C7A6B]">
            <SearchIcon />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Rechercher un produit"
              className="min-w-0 flex-1 bg-transparent font-sans text-[14px] text-[#354A31] outline-none placeholder:text-[#9AA39A]"
            />
          </label>
        </div>
      </div>

      <div
        className={cn(
          COLUMNS,
          'shrink-0 px-[8px] font-sans text-[14px] leading-[16px] font-medium text-[#6C7A6B]',
        )}
      >
        <span>Catégorie / produit</span>
        <span>Stock</span>
        <span>Vendus</span>
        <span>Rupture</span>
        <span />
      </div>

      <div className="-mr-[12px] flex min-h-0 flex-1 flex-col gap-[8px] overflow-y-auto kiosk-scroll-y pr-[12px]">
        {groups.length === 0 ? (
          <span className="py-[24px] text-center font-sans text-[14px] font-medium text-[#7C867D]">
            {filtering ? 'Aucun produit ne correspond' : 'Aucun stock en boutique'}
          </span>
        ) : (
          groups.map(({ category, products }) => (
            <CategoryGroup
              key={category.id}
              category={category}
              products={products}
              open={filtering || openIds.has(category.id)}
              onToggle={() => toggle(category.id)}
            />
          ))
        )}
      </div>
    </ModalPanel>
  )
}

/**
 * Modale « Stock restant » (maquette validée par le client), ouverte par le chevron de la carte
 * STOCK RESTANT : synthèse (UVC, références en stock, stock faible, ruptures, prochaine livraison)
 * puis le détail par catégorie et produit (stock, vendus du jour, heure de rupture prévue), avec
 * filtres et recherche.
 */
export function StockDetailModal({ onClose }: { onClose: () => void }) {
  const { data, dataUpdatedAt, isError } = useStoreStockDetail()

  return (
    <KioskModal
      icon={<LayerIcon className="size-[24px]" />}
      title="Stock restant"
      ariaLabel="Détail du stock restant"
      onClose={onClose}
      className="h-[1180px] w-[1100px]"
    >
      <ModalBody
        data={data}
        isError={isError}
        errorLabel="Stock indisponible pour le moment"
        className="min-h-0 flex-1"
        skeleton={
          <div className="flex min-h-0 flex-1 flex-col gap-[12px]" aria-busy>
            <Skeleton className="h-[96px] w-full rounded-[16px]" />
            <Skeleton className="min-h-0 w-full flex-1 rounded-[16px]" />
          </div>
        }
      >
        {(loaded) => (
          <div className="flex min-h-0 flex-1 flex-col gap-[12px]">
            <Summary data={loaded} updatedAt={dataUpdatedAt} />
            <ProductDetail data={loaded} />
          </div>
        )}
      </ModalBody>
    </KioskModal>
  )
}
